import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import useProgresso from '../../hooks/useProgresso';
import Sidebar from '../../components/Sidebar';
import HeaderProgresso from '../../components/HeaderProgresso';
import DadosGerais from './DadosGerais';
import Empresas from './Empresas';
import Modulos from './Modulos';
import Anexos from './Anexos';
import Usuarios from './Usuarios';

function Spinner() {
  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50">
      <div className="w-10 h-10 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
    </div>
  );
}

function ErroCarregamento({ onTentar }) {
  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50 p-6">
      <div className="card max-w-md w-full text-center space-y-4">
        <div className="text-4xl">⚠️</div>
        <h2 className="text-lg font-semibold text-gray-900">Erro ao carregar o portal</h2>
        <p className="text-sm text-gray-500">
          Não foi possível carregar os dados do onboarding. Verifique sua conexão e tente novamente.
        </p>
        <button onClick={onTentar} className="btn-primary justify-center">
          Tentar novamente
        </button>
      </div>
    </div>
  );
}

export default function Portal() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dados, setDados] = useState(null);
  const [modulos, setModulos] = useState([]);
  const [anexosPadrao, setAnexosPadrao] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [secaoAtiva, setSecaoAtiva] = useState('dados-gerais');

  const { percentual, porSecao } = useProgresso(dados, modulos, anexosPadrao);

  const carregarDados = useCallback(async () => {
    setErro(false);
    try {
      const { data } = await api.get(`/onboardings/${id}`);
      setDados(data);
      setModulos(data.modulos || []);
      setAnexosPadrao(data.anexosPadrao || []);
    } catch {
      setErro(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    async function inicializar() {
      setLoading(true);
      await carregarDados();
      // Registra login de forma silenciosa — não bloqueia o carregamento
      api.post(`/onboardings/${id}/registrar-login`).catch(() => {});
    }
    inicializar();
  }, [id, carregarDados]);

  function handleSair() {
    localStorage.removeItem('ht_token');
    localStorage.removeItem('ht_usuario');
    navigate('/login');
  }

  if (loading) return <Spinner />;
  if (erro) return <ErroCarregamento onTentar={() => { setLoading(true); carregarDados(); }} />;
  if (!dados) return null;

  const nomeEmpresa = dados.nomeFantasia || dados.razaoSocial || 'Portal de Onboarding';

  const propsComunsPorSecao = {
    dados,
    onboardingId: id,
    atualizarDados: carregarDados,
  };

  function renderizarSecao() {
    switch (secaoAtiva) {
      case 'dados-gerais':
        return <DadosGerais {...propsComunsPorSecao} />;
      case 'empresas':
        return <Empresas {...propsComunsPorSecao} />;
      case 'modulos':
        return <Modulos {...propsComunsPorSecao} modulos={modulos} />;
      case 'anexos':
        return <Anexos {...propsComunsPorSecao} anexosPadrao={anexosPadrao} />;
      case 'usuarios':
        return <Usuarios {...propsComunsPorSecao} />;
      default:
        return <DadosGerais {...propsComunsPorSecao} />;
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <HeaderProgresso
        empresa={nomeEmpresa}
        percentual={percentual}
        onSair={handleSair}
      />

      {dados.finalizado && (
        <div className="bg-green-700 text-white text-sm font-medium px-6 py-2.5 text-center">
          Seu onboarding foi finalizado pela HeadThink. Esta é uma visualização somente leitura.
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        <Sidebar
          secaoAtiva={secaoAtiva}
          onNavegar={setSecaoAtiva}
          porSecao={porSecao}
          finalizado={!!dados.finalizado}
        />

        <main className="flex-1 overflow-auto px-6 py-8 max-w-3xl mx-auto w-full">
          {renderizarSecao()}
        </main>
      </div>
    </div>
  );
}
